import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { AlertCircle, ArrowRight, Eye, EyeOff, Lock, User } from 'lucide-react';
const VALID_USERNAME = 'marketing@vexu.ai';
const VALID_PASSWORD = 'VeXu@202600';
interface AppLoginPageProps {
  onLogin: () => void;
}
export function AppLoginPage({
  onLogin
}: AppLoginPageProps) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    window.setTimeout(() => {
      setIsLoading(false);
      if (username.trim().toLowerCase() === VALID_USERNAME && password === VALID_PASSWORD) {
        onLogin();
      } else {
        setError('Incorrect username or password. Please try again.');
      }
    }, 800);
  };
  return <main className="min-h-screen w-full bg-dark flex items-center justify-center px-5 py-10 relative overflow-hidden" data-id="element-737" data-mp="2qj0uwee2d067">
      {/* Ambient background */}
      <div className="absolute -top-32 -right-24 w-[420px] h-[420px] rounded-full bg-vexu/10 blur-3xl pointer-events-none" data-id="element-738" data-mp="2lpy248h41ur2" />
      <div className="absolute -bottom-40 -left-28 w-[380px] h-[380px] rounded-full bg-blue-500/5 blur-3xl pointer-events-none" data-id="element-739" data-mp="2crmdgbzt1719" />

      <motion.section initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.45,
      ease: [0.22, 1, 0.36, 1]
    }} className="relative z-10 w-full max-w-[400px]" data-id="element-740" data-mp="39gk87u7k14yf">
        <header className="mb-8 text-center" data-id="element-741" data-mp="30f3yzpsh78vs">
          <img src="https://cdn.magicpatterns.com/uploads/3A8d459aXLumR8KKFCQtrD/vexu_logo.svg" alt="Vexu.ai" className="w-12 h-12 object-contain mx-auto mb-6" data-id="element-742" data-mp="2vidhqz4xq78s" />
          <h1 className="text-2xl font-bold text-white tracking-tight" data-id="element-743" data-mp="1k2fcl6cvo5cu">
            Sign in to Vexu.ai
          </h1>
          <p className="mt-2 text-sm text-zinc-400" data-id="element-744" data-mp="1ll5le8r4hgsb">
            Enter your credentials to access the app and demo console.
          </p>
        </header>

        <form onSubmit={handleSubmit} noValidate className="bg-dark-card/70 backdrop-blur-xl border border-white/10 rounded-2xl p-6 shadow-2xl shadow-black/60 space-y-5" data-id="element-745" data-mp="1cjfwb9kpqtxp">
          <div data-id="element-746" data-mp="12sup4xqz4h36">
            <label htmlFor="username" className="block text-sm font-medium text-zinc-300 mb-1.5" data-id="element-747" data-mp="12sbhqayy9b3e">
              Username
            </label>
            <div className="relative" data-id="element-748" data-mp="svoxnoeg6o2f">
              <User className="w-5 h-5 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-749" data-mp="swrpbyjlguje" />
              <input id="username" type="text" autoComplete="username" autoCapitalize="none" spellCheck={false} placeholder="marketing@vexu.ai" value={username} onChange={e => {
              setUsername(e.target.value);
              if (error) setError('');
            }} required className="w-full rounded-xl bg-black/40 border border-white/10 text-white placeholder:text-zinc-600 pl-10 pr-4 py-3 outline-none transition-all focus:border-vexu focus:ring-2 focus:ring-vexu/20" data-id="element-750" data-mp="xut5aalq4dqn" />
            </div>
          </div>

          <div data-id="element-751" data-mp="o532mz2islm5">
            <label htmlFor="password" className="block text-sm font-medium text-zinc-300 mb-1.5" data-id="element-752" data-mp="o4kwnaq22t11">
              Password
            </label>
            <div className="relative" data-id="element-753" data-mp="xvxpa4john84">
              <Lock className="w-5 h-5 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-754" data-mp="sxdy9pq89l8w" />
              <input id="password" type={showPassword ? 'text' : 'password'} autoComplete="current-password" placeholder="••••••••••••" value={password} onChange={e => {
              setPassword(e.target.value);
              if (error) setError('');
            }} required className="w-full rounded-xl bg-black/40 border border-white/10 text-white placeholder:text-zinc-600 pl-10 pr-11 py-3 outline-none transition-all focus:border-vexu focus:ring-2 focus:ring-vexu/20" data-id="element-755" data-mp="16x5b3n50cvlb" />
              <button type="button" onClick={() => setShowPassword(s => !s)} aria-label={showPassword ? 'Hide password' : 'Show password'} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-md text-zinc-500 hover:text-zinc-200 transition-colors" data-id="element-756" data-mp="2dg6prdbntfyv">
                {showPassword ? <EyeOff className="w-5 h-5" data-id="element-757" data-mp="1q4bxkzrwxyp" /> : <Eye className="w-5 h-5" data-id="element-758" data-mp="1rymc5df57oj" />}
              </button>
            </div>
          </div>

          {error && <motion.p initial={{
          opacity: 0,
          y: -4
        }} animate={{
          opacity: 1,
          y: 0
        }} role="alert" className="flex items-start gap-2 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 px-3 py-2.5 text-sm" data-id="element-759" data-mp="gtf7h7fxbnie">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-760" data-mp="31qvr59l711y" />
              <span data-id="element-761" data-mp="2zehp36y9au6">{error}</span>
            </motion.p>}

          <button type="submit" disabled={isLoading} className="w-full h-12 inline-flex items-center justify-center gap-2 rounded-xl bg-vexu text-black font-semibold shadow-lg shadow-vexu/20 transition-all hover:bg-vexu-400 active:scale-[0.99] disabled:opacity-60 disabled:pointer-events-none focus:outline-none focus:ring-2 focus:ring-vexu focus:ring-offset-2 focus:ring-offset-dark" data-id="element-762" data-mp="3u8la24tay20z">
            {isLoading ? 'Signing in…' : 'Sign In'}
            {!isLoading && <ArrowRight className="w-5 h-5" data-id="element-763" data-mp="1ehz2zjzyr0jy" />}
          </button>
        </form>

        <p className="mt-6 text-center text-xs text-zinc-600" data-id="element-764" data-mp="37z4vuynpoitp">
          Protected preview · access limited to authorized Vexu.ai accounts
        </p>
      </motion.section>
    </main>;
}